/**
 * Naming the locale a published wording came in.
 *
 * Both consent screens showed the raw tag — `Published in <code>en</code>` on
 * sign-up, and the same in Settings' footer (#137). `en` is a machine token,
 * and this is a screen written for someone about to attest to having read a
 * legal text; the one line telling them which language it is in should be in a
 * language.
 *
 * # The tag is still the fact, and it is not thrown away
 *
 * `Intl.DisplayNames` is a rendering of the tag, not a replacement for it: it
 * is the runtime's table, it varies by engine and by ICU version, and it can
 * answer nothing at all. So the name is shown and the tag is kept beside it
 * wherever the screen has room, and an unresolvable tag degrades to itself
 * rather than to a guess or to an empty string.
 *
 * # Why this is not the `?? 'EUR'` mistake
 *
 * It reads like a default and it is not one. `CLAUDE.md` §3 forbids defaulting
 * a currency and §5 forbids defaulting a country because those values decide
 * what is owed and which rules apply — substituting one silently is a decision
 * taken on somebody's behalf with no record of the guess. Nothing branches on
 * this string. It is the label on a text whose bytes are hashed elsewhere, and
 * the fallback here is the input unchanged, which invents nothing.
 */
export function languageName(locale: string): string {
  const tag = locale.trim()
  if (tag === '') return ''
  let name: string | undefined
  try {
    // `type: 'language'` on a full tag like `en-GB` yields "British English"
    // where the bare `en` yields "English"; both are the truth about the tag
    // rather than a normalisation of it.
    name = new Intl.DisplayNames(['en'], { type: 'language' }).of(tag)
  } catch {
    // A malformed tag throws RangeError, and an engine without the table
    // throws too. Neither is worth failing a render over.
    return tag
  }
  // `of()` returns the input unchanged when it has no name for it, and may
  // return undefined. Both mean "no name", and both must show the tag alone
  // rather than "en (en)".
  if (name === undefined || name === tag) return tag
  return `${name} (${tag})`
}
